import React, { useEffect, useState, useRef } from "react";
import api from "../utils/api";

import {
  FaFolder,
  FaFolderPlus,
  FaUpload,
  FaTrash,
  FaEye,
  FaFileAlt,
  FaSpinner,
  FaArrowLeft,
  FaFilePdf,
  FaFileWord,
  FaImage,
  FaDownload,
} from "react-icons/fa";

function FileManager() {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [previewFile, setPreviewFile] = useState(null);

  const dropZoneRef = useRef();

  /* =======================================================
      Fetch Files
  ======================================================= */
  const fetchFiles = async () => {
    try {
      const res = await api.get("/files");
      const data = Array.isArray(res.data) ? res.data : [];
      setFiles(data);
      setFolders([...new Set(data.map(f => f.folder || "عام"))]);
    } catch (err) {
      console.error(err);
      alert("⚠️ فشل تحميل الملفات");
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  /* =======================================================
      Upload File
  ======================================================= */
  const handleUpload = async (file) => {
    if (!file || !currentFolder) return alert("اختر مجلد أولاً");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", currentFolder);

    try {
      setUploading(true);
      await api.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchFiles();
      alert("✔ تم رفع الملف");
    } catch (err) {
      console.error(err);
      alert("⚠️ فشل الرفع");
    } finally {
      setUploading(false);
    }
  };

  /* =======================================================
      Drag & Drop
  ======================================================= */
  const handleDrop = (e) => {
    e.preventDefault();
    handleUpload(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => e.preventDefault();

  /* =======================================================
      Delete
  ======================================================= */
  const handleDelete = async (id) => {
    if (!window.confirm("هل تريد حذف الملف؟")) return;
    try {
      await api.delete(`/files/${id}`);
      fetchFiles();
    } catch (err) {
      alert("⚠️ فشل الحذف");
    }
  };

  /* =======================================================
      File Icons
  ======================================================= */
  const getFileIcon = (type) => {
    if (!type) return <FaFileAlt className="text-gray-700 text-4xl" />;

    if (type.includes("pdf")) return <FaFilePdf className="text-red-500 text-4xl" />;
    if (type.includes("word") || type.includes("doc"))
      return <FaFileWord className="text-blue-600 text-4xl" />;
    if (type.includes("image")) return <FaImage className="text-yellow-500 text-4xl" />;

    return <FaFileAlt className="text-gray-700 text-4xl" />;
  };

  /* =======================================================
      Render
  ======================================================= */
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">

      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-2xl p-6">

        {/* Title */}
        <h1 className="text-3xl font-bold text-center text-yellow-600 mb-6">
          📁 مدير الملفات
        </h1>

        {/* ---------------- Folders ---------------- */}
        {!currentFolder && (
          <div>
            {/* Create Folder */}
            <div className="flex gap-3 mb-6">
              <input
                className="border p-3 rounded-lg flex-1 text-black"
                placeholder="اسم المجلد..."
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
              />
              <button
                className="bg-yellow-500 px-4 py-2 rounded-lg text-black font-bold"
                onClick={() => {
                  if (!newFolderName.trim()) return;
                  if (folders.includes(newFolderName)) return;
                  setFolders([...folders, newFolderName]);
                  setNewFolderName("");
                }}
              >
                <FaFolderPlus />
              </button>
            </div>

            {/* Folder List */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {folders.map((folder, i) => (
                <div
                  key={i}
                  className="bg-yellow-100 hover:bg-yellow-200 p-4 rounded-xl text-center cursor-pointer shadow"
                  onClick={() => setCurrentFolder(folder)}
                >
                  <FaFolder className="text-yellow-600 text-5xl mx-auto mb-2" />
                  <p className="font-bold text-black">{folder}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---------------- Files ---------------- */}
        {currentFolder && (
          <>
            <div className="flex justify-between items-center mb-6">
              <button
                className="text-yellow-600 flex items-center gap-2"
                onClick={() => setCurrentFolder(null)}
              >
                <FaArrowLeft /> رجوع
              </button>
              <h2 className="text-lg font-bold text-black">{currentFolder}</h2>
            </div>

            {/* Upload */}
            <div
              ref={dropZoneRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-yellow-500 p-8 rounded-xl text-center bg-yellow-50 mb-6"
            >
              <FaUpload className="text-yellow-600 text-3xl mx-auto mb-3" />
              <p className="mb-3 text-black">اسحب الملف هنا</p>
              <input
                type="file"
                className="text-black"
                onChange={(e) => handleUpload(e.target.files[0])}
              />

              {uploading && (
                <div className="mt-3 text-yellow-600 flex items-center justify-center gap-2">
                  <FaSpinner className="animate-spin" /> جارِ الرفع...
                </div>
              )}
            </div>

            {/* File Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {files
                .filter((f) => f.folder === currentFolder)
                .map((file) => (
                  <div
                    key={file._id}
                    className="border bg-gray-100 p-4 rounded-xl shadow text-center"
                  >
                    {getFileIcon(file.type)}

                    <p className="mt-2 text-black truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>

                    <div className="mt-3 flex flex-col gap-2">

                      {/* VIEW */}
                      <button
                        className="bg-blue-500 text-white py-1 rounded"
                        onClick={() => setPreviewFile(file.url)}
                      >
                        عرض
                      </button>

                      {/* DOWNLOAD */}
                      <a
                        className="bg-gray-700 text-white py-1 rounded"
                        href={file.url}
                        download
                      >
                        تحميل
                      </a>

                      {/* DELETE */}
                      <button
                        className="bg-red-500 text-white py-1 rounded"
                        onClick={() => handleDelete(file._id)}
                      >
                        حذف
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}

        {/* ---------------- Preview Modal ---------------- */}
        {previewFile && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-2xl rounded-xl p-4 relative">
              <button
                className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded"
                onClick={() => setPreviewFile(null)}
              >
                إغلاق ✖
              </button>

              <embed
                src={previewFile}
                type="application/pdf"
                className="w-full h-[75vh] rounded"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FileManager;
